import { FORM_ERROR } from "final-form"
import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from "axios"
import { camelizeKeys, decamelize, decamelizeKeys } from "humps"
import { toast } from "react-toastify";
import ToastMessageBody from "../components/texts/ToastMessageBody";
import { FileArray } from "../types/loan";
import { loadLocalizedRoot } from "../utils/navigation";

export interface RedirectUrl {
  redirectUrl?: string;
}

export interface FlashMessage {
  flash?: {
    debug?: string;
    error?: string;
    success?: string;
  }
}

export function getCsrfToken(): string {
  const csrfMeta: HTMLMetaElement | null = document.querySelector("meta[name='csrf-token']")
  return csrfMeta?.content || ""
}

export const basePath = "/internal_api"

export const formApi = axios.create({
  baseURL: "/",
  headers: {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
    "X-CSRF-Token": getCsrfToken(),
    "X-Requested-With": "XMLHttpRequest",
  },
  maxRedirects: 0,
})

export const jsonApi = axios.create({
  baseURL: "/",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "X-CSRF-Token": getCsrfToken(),
    "X-Requested-With": "XMLHttpRequest",
  },
  maxRedirects: 0,
})

export const getResponseData = <T>(response: AxiosResponse<T>): T => response.data;

const camelizeResponse = (response: AxiosResponse): AxiosResponse => {
  return { ...response, data: camelizeKeys(response.data) }
}

const decamelizeRequest = (request: AxiosRequestConfig): AxiosRequestConfig => {
  return { ...request, data: decamelizeKeys(request.data) }
}

const showFlash = (data?: FlashMessage): void => {
  const flash = data?.flash;
  if (!flash) return;
  if (flash.debug) setTimeout(() => alert(flash.debug), 200);
  if (flash.error) toast.error(ToastMessageBody({ message: flash.error, svgSymbolId: "warning" }));
  if (flash.success) toast.success(ToastMessageBody({ message: flash.success, svgSymbolId: "success-message" }));
}

const successResponseInterceptor = (response: AxiosResponse): AxiosResponse => {
  showFlash(response?.data);
  return camelizeResponse(response);
}

type ResponseError =
  | { errors: FormErrors }
  | Record<string, string>
  | string
  | null
  | undefined;

const errorsFromResponse = (response: AxiosResponse<ResponseError>): FormErrors => {
  const { data } = response;
  if (!data) return {};
  if (typeof data === "string") return { error: data };
  return camelizeKeys(typeof data.errors === "object" ? data.errors : data);
}

const errorResponseInterceptor = (error: AxiosError): Promise<never> => {
  const { response } = error
  showFlash(response?.data);
  if (response && response.status >= 400 && response.status < 500) {
    const errors: FormErrors = errorsFromResponse(response);
    if (response.status === 403 && errors?.errorType === "user_blocked") {
      loadLocalizedRoot();
    }
    errors.message = errors.error;
    errors[FORM_ERROR] = errors.base || errors.error
    return Promise.reject(errors)
  }
  if (response) console.log(error);
  return Promise.reject(error)
}

jsonApi.interceptors.request.use(decamelizeRequest)
jsonApi.interceptors.response.use(successResponseInterceptor, errorResponseInterceptor)
formApi.interceptors.response.use(successResponseInterceptor, errorResponseInterceptor)

export type OnSuccess<D = unknown, R = void> = null | string | ((data?: D) => R)

const emptyFunc = () => {};

export const successFunc = <D = unknown, R = void>(onSuccessParam?: OnSuccess<D, R>) => {
  if (onSuccessParam == null) return emptyFunc;
  if (typeof(onSuccessParam) === "function") return onSuccessParam;
  return () => { document.location.pathname = onSuccessParam };
}

export const handleFormErrors = (errors: FormErrors) => errors;

type Value = boolean | null | number | string | undefined | FileInfo;

const serializeValue = (value?: Value): Blob | string => {
  if (value == null) return "";
  return typeof(value) === "object" ? (value.selectedFile || "") : String(value)
}

export type FormValues = Record<string, Value | FileInfo[] | string[]>;

export const appendToForm = (form: FormData, prefix: string, obj: FormValues): void => {
  for (const key in obj) {
    const data = obj[key];
    if (Array.isArray(data)){
      data.forEach(item => {
        form.append(`${prefix}[${decamelize(key)}][]`, serializeValue(item));
      });
    } else {
      form.append(`${prefix}[${decamelize(key)}]`, serializeValue(data));
    }
  }
}

const digiDocExt = /\.(asice|bdoc|ddoc)$/i;

export const fileToUpload = (file: File): File => {
  if (digiDocExt.test(file.name)) {
    return new File([file], file.name, { type: "application/vnd.etsi.asic-e+zip" });
  }
  return file;
}

export const appendFileToForm = (form: FormData, prefix: string, name: string, fileInfo: FileInfo): void => {
  if (fileInfo.id) form.append(`${prefix}[id_${name}]`, fileInfo.id.toString());
  if (fileInfo.selectedFile) form.append(`${prefix}[${name}]`, fileToUpload(fileInfo.selectedFile));
};

export const apppenrFileArrayToForm = (form: FormData, prefix: string, files: FileArray, name: string) => {
  files.forEach(data => {
    const file = data?.file;
    if (file?.selectedFile) {
      form.append(`${prefix}[${name}][]`, file.selectedFile);
    }
    else {
      form.append(`${prefix}[${name}][]`, "");
    }
  });
};
